/**
 * nav-abort.ts — tell a "the user navigated away" read failure apart from a
 * genuine one, so navigation-teardown noise logs at debug while real failures
 * stay errors (Rule 8).
 *
 * Rapid route changes / hard navigations abort in-flight fetches. The browser
 * reports `net::ERR_ABORTED`, which surfaces to JS as `TypeError: Failed to
 * fetch` — INDISTINGUISHABLE from a genuine offline failure by the error object
 * alone. So we classify by CONTEXT: any of three signals means "abort, not a
 * real failure":
 *
 *   1. The error is a named AbortError (an explicit `AbortController.abort()`).
 *   2. The document is unloading — a `pagehide` fired, i.e. a hard navigation /
 *      tab close is tearing the page down.
 *   3. The caller's surface was superseded — it passes `mounted = false`, i.e.
 *      an effect cleanup ran / the component unmounted on an SPA route change.
 *
 * A mounted, page-live "Failed to fetch" matches NONE of these and stays an
 * error: genuine offline is still surfaced. This is deliberately conservative —
 * it never blanket-swallows a non-abort failure.
 *
 * QA ref: 2026-07-19 nav-stress sweep — rapid route changes logged aborted
 * rpc/group/list, rpc/feed/list, rpc/friend/hasFriends, rpc/ratingSchema/list,
 * and recipe.get failures as console errors, drowning real errors in the noise.
 */

import { isAbortError } from "@proof/client-core/errors";

let pageUnloading = false;

function install(): void {
  if (typeof window === "undefined") return;
  // `pagehide` (not `beforeunload`) — it fires on both unload AND bfcache-enter
  // and, unlike `beforeunload`, does not disqualify the page from the bfcache.
  window.addEventListener("pagehide", () => {
    pageUnloading = true;
  });
  // A page restored from the bfcache is live again — clear the flag.
  window.addEventListener("pageshow", () => {
    pageUnloading = false;
  });
}
install();

/**
 * True when a rejected read is navigation / unmount teardown rather than a real
 * failure the user should be told about. Pass the surface's live-ness as
 * `mounted` (default `true`) — e.g. `!cancelled` from an effect, or
 * `mountedRef.current` from a component.
 */
export function isNavigationAbort(err: unknown, mounted = true): boolean {
  if (!mounted) return true;
  if (pageUnloading) return true;
  return isAbortError(err);
}

/** Test-only: reset the module-level page-unloading flag between cases. */
export function __resetNavAbortForTest(): void {
  pageUnloading = false;
}
